import { useMemo } from 'react'
import type { LumpsumIdealCategory } from '../../../api/ideal.types'
import { PlansPlanMode } from '../../../api/types'
import {
  isIdealPlansFetchFailed,
  isIdealPlansLoading,
  useLumpsumPlansContext
} from '../../LumpsumPlansProvider'
import { PlansListEmptyState } from '../../lumpsum-ui-components/PlansListEmptyState'
import { PlansCategoryListSkeleton } from '../../lumpsum-ui-components/PlansCategoryListSkeleton'
import { IdealCategoryCard } from './IdealCategoryCard'
import { IdealPlansListErrorState } from './IdealPlansListErrorState'
import { isIdealPlansListFullyEmpty } from '../../../../utils/lumpsum/planListUtils'

type IdealCategoryListProps = {
  /** When set, render these instead of context and skip query loading/error/empty shells. */
  categories?: LumpsumIdealCategory[] | null
}

export function IdealCategoryList({ categories: dummyCategories }: IdealCategoryListProps = {}) {
  const { idealData, idealPlansQuery } = useLumpsumPlansContext()
  const hasDummyCategories = dummyCategories !== undefined

  const idealCategories = useMemo(() => {
    const categories = hasDummyCategories
      ? (dummyCategories ?? [])
      : (idealData?.categories ?? [])
    if (!categories.length) {
      return []
    }
    return categories.filter((category) => (category?.idealFunds ?? []).length > 0)
  }, [hasDummyCategories, dummyCategories, idealData?.categories])

  if (!hasDummyCategories) {
    if (isIdealPlansLoading(PlansPlanMode.IDEAL_PORTFOLIO, idealData != null, idealPlansQuery)) {
      return <PlansCategoryListSkeleton showBuckets={false} />
    }

    if (isIdealPlansFetchFailed(PlansPlanMode.IDEAL_PORTFOLIO, idealPlansQuery)) {
      return <IdealPlansListErrorState onRetry={idealPlansQuery.refetch} />
    }

    if (isIdealPlansListFullyEmpty(idealData?.categories)) {
      return (
        <PlansListEmptyState
          planMode={PlansPlanMode.IDEAL_PORTFOLIO}
          planSnapshotDate={idealData?.allocationPlanSnapshotDate}
          planDurationInMonths={idealData?.planDurationInMonths}
        />
      )
    }
  }

  return (
    <>
      <IdealCategoryListStickyBar categoryCount={idealCategories.length} />
      {idealCategories.map((category, categoryIndex) => (
        <IdealCategoryCard
          key={category?.categoryId ?? `ideal-category-${categoryIndex}`}
          category={category}
          categoryIndex={categoryIndex}
          noDivider={categoryIndex === 0}
        />
      ))}
    </>
  )
}

export function IdealCategoryListStickyBar({ categoryCount }: { categoryCount: number }) {
  return (
    <div className="sticky top-[-2px] z-10 flex items-center justify-between bg-white px-5 py-4 border-b border-[#E8EBEA]">
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">
        {categoryCount} {categoryCount === 1 ? 'Category' : 'Categories'}
      </div>
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">Ideal range</div>
    </div>
  )
}
